import { useEffect, useState } from 'react'
import { AxiosResponse } from 'axios'
import pclnRequest from '@pcln/request'
import analytics from '@/shared-utils/analytics'
import hasGraphErrors from '@/shared-utils/graph-utils'
import config from 'isomorphic-config'
import { GraphResponse } from '@/types'
import SMS_DISCLAIMER_TEXT_INITIAL_VALUE from './constants'
import type { DisclaimerParams } from './types'
import useBootstrapData from '../../hooks/useBootstrapData'

type DisclaimerDetail = Readonly<{
  optinStatus: boolean | null
  disclaimerText: ReadonlyArray<{
    text: string
    url: string | null
  } | null> | null
}>

type DisclaimerQueryResultWithEmail = {
  readonly smsDisclaimerTextByEmail: DisclaimerResponse | null
}

type DisclaimerQueryResult = {
  readonly smsDisclaimerText: DisclaimerResponse | null
}

type DisclaimerResponse = {
  readonly disclaimers: ReadonlyArray<DisclaimerDetail | null> | null
}

type DisclaimerGraphResponse =
  | GraphResponse<DisclaimerQueryResultWithEmail>
  | GraphResponse<DisclaimerQueryResult>

function isDisclaimerDetail(
  payload: DisclaimerDetail | null
): payload is DisclaimerDetail {
  return payload !== null
}

function extractValidDisclaimers(
  data: DisclaimerQueryResultWithEmail | DisclaimerQueryResult
) {
  if ('smsDisclaimerTextByEmail' in data) {
    if (
      data.smsDisclaimerTextByEmail &&
      data.smsDisclaimerTextByEmail.disclaimers
    ) {
      return data.smsDisclaimerTextByEmail.disclaimers.filter(
        isDisclaimerDetail
      )
    }
  }
  if ('smsDisclaimerText' in data) {
    if (data.smsDisclaimerText && data.smsDisclaimerText.disclaimers) {
      return data.smsDisclaimerText.disclaimers.filter(isDisclaimerDetail)
    }
  }

  return null
}

function useGetDisclaimer(params: DisclaimerParams) {
  const { appName, appVersion } = useBootstrapData()
  const [disclaimers, setDisclaimers] = useState<
    ReadonlyArray<DisclaimerDetail>
  >(SMS_DISCLAIMER_TEXT_INITIAL_VALUE)

  useEffect(() => {
    async function fetchSmsDisclaimer() {
      try {
        const { url, timeout } = config.client['pcln-graph']
        const response: AxiosResponse<DisclaimerGraphResponse> =
          await pclnRequest({
            url: `${url}?gqlOp=${params.operationName}`,
            timeout,
            method: 'POST',
            headers: {
              'apollographql-client-name': appName,
              'apollographql-client-version': appVersion
            },
            data: {
              query: params.query,
              variables: params.disclaimerParams
            }
          })
        if (hasGraphErrors(response.data)) {
          const { errors } = response.data
          const firstErrorMessage = errors[0]?.message
          const errorMessage = firstErrorMessage
            ? `Error message: ${firstErrorMessage}`
            : 'Unknown error'
          const errorObjectAsString = JSON.stringify(errors)
          const message = `The query for SMS disclaimer text has returned errors. ${errorMessage}`
          analytics.logError({
            message,
            cguid: params.cguid,
            ...params.disclaimerParams.disclaimerBaseParam,
            email: params.disclaimerParams.emailAddress ?? '',
            errorObjectAsString
          })
          return
        }
        const { data } = response.data
        if (data) {
          // TODO remove success log in seti cleanup. seti: HP_SMS_COMPONENT
          analytics.log({
            message: `Fetched disclaimer for cGuid: ${params.cguid}`
          })
          const validDisclaimers = extractValidDisclaimers(data)
          if (validDisclaimers) {
            setDisclaimers(validDisclaimers)
          }
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : error?.toString() ?? 'Unknown error'
        analytics.logError({
          message: `Failed to query for SMS disclaimer text for cGuid: ${params.cguid}. Error message: ${errorMessage}`
        })
      }
    }
    void fetchSmsDisclaimer()
  }, [params, appName, appVersion])

  return disclaimers
}

export default useGetDisclaimer
